/*
 * =====================================================================================
 * DISCLAIMER:
 * =====================================================================================
 * This localization file is a community contribution and is not officially maintained
 * by Clerk. It has been provided by the community and may not be fully aligned
 * with the current or future states of the main application. Clerk does not guarantee
 * the accuracy, completeness, or timeliness of the translations in this file.
 * Use of this file is at your own risk and discretion.
 * =====================================================================================
 */

import type { LocalizationResource } from '@clerk/types';

export const koKR: LocalizationResource = {
  locale: 'ko-KR',
  apiKeys: {
    action__add: undefined,
    action__search: undefined,
    createdAndExpirationStatus__expiresOn: undefined,
    createdAndExpirationStatus__never: undefined,
    detailsTitle__emptyRow: undefined,
    formButtonPrimary__add: undefined,
    formFieldCaption__expiration__expiresOn: undefined,
    formFieldCaption__expiration__never: undefined,
    formFieldOption__expiration__180d: undefined,
    formFieldOption__expiration__1d: undefined,
    formFieldOption__expiration__1y: undefined,
    formFieldOption__expiration__30d: undefined,
    formFieldOption__expiration__60d: undefined,
    formFieldOption__expiration__7d: undefined,
    formFieldOption__expiration__90d: undefined,
    formFieldOption__expiration__never: undefined,
    formHint: undefined,
    formTitle: undefined,
    lastUsed__days: undefined,
    lastUsed__hours: undefined,
    lastUsed__minutes: undefined,
    lastUsed__months: undefined,
    lastUsed__seconds: undefined,
    lastUsed__years: undefined,
    menuAction__revoke: undefined,
    revokeConfirmation: {
      confirmationText: undefined,
      formButtonPrimary__revoke: undefined,
      formHint: undefined,
      formTitle: undefined,
    },
  },
  backButton: '돌아가기',
  badge__activePlan: undefined,
  badge__canceledEndsAt: undefined,
  badge__currentPlan: undefined,
  badge__default: '기본값',
  badge__endsAt: undefined,
  badge__expired: undefined,
  badge__freeTrial: undefined,
  badge__otherImpersonatorDevice: '기타 사칭 장치',
  badge__pastDueAt: undefined,
  badge__pastDuePlan: undefined,
  badge__primary: '기본',
  badge__renewsAt: undefined,
  badge__requiresAction: '조치 필요',
  badge__startsAt: undefined,
  badge__thisDevice: '이 장치',
  badge__trialEndsAt: undefined,
  badge__unverified: '미확인',
  badge__upcomingPlan: undefined,
  badge__userDevice: '사용자 장치',
  badge__you: '당신',
  commerce: {
    addPaymentMethod: undefined,
    alwaysFree: undefined,
    annually: undefined,
    availableFeatures: undefined,
    billedAnnually: undefined,
    billedMonthlyOnly: undefined,
    cancelFreeTrial: undefined,
    cancelFreeTrialAccessUntil: undefined,
    cancelFreeTrialTitle: undefined,
    cancelSubscription: undefined,
    cancelSubscriptionAccessUntil: undefined,
    cancelSubscriptionNoCharge: undefined,
    cancelSubscriptionTitle: undefined,
    cannotSubscribeMonthly: undefined,
    cannotSubscribeUnrecoverable: undefined,
    checkout: {
      description__paymentSuccessful: undefined,
      description__subscriptionSuccessful: undefined,
      downgradeNotice: undefined,
      emailForm: {
        subtitle: undefined,
        title: undefined,
      },
      lineItems: {
        title__freeTrialEndsAt: undefined,
        title__paymentMethod: undefined,
        title__statementId: undefined,
        title__subscriptionBegins: undefined,
        title__totalPaid: undefined,
      },
      pastDueNotice: undefined,
      perMonth: undefined,
      title: undefined,
      title__paymentSuccessful: undefined,
      title__subscriptionSuccessful: undefined,
      title__trialSuccess: undefined,
      totalDueAfterTrial: undefined,
    },
    credit: undefined,
    creditRemainder: undefined,
    defaultFreePlanActive: undefined,
    free: undefined,
    getStarted: undefined,
    keepFreeTrial: undefined,
    keepSubscription: undefined,
    manage: undefined,
    manageSubscription: undefined,
    month: undefined,
    monthly: undefined,
    pastDue: undefined,
    pay: undefined,
    paymentMethods: undefined,
    paymentSource: {
      applePayDescription: {
        annual: undefined,
        monthly: undefined,
      },
      dev: {
        anyNumbers: undefined,
        cardNumber: undefined,
        cvcZip: undefined,
        developmentMode: undefined,
        expirationDate: undefined,
        testCardInfo: undefined,
      },
    },
    popular: undefined,
    pricingTable: {
      billingCycle: undefined,
      included: undefined,
    },
    reSubscribe: undefined,
    seeAllFeatures: undefined,
    startFreeTrial: undefined,
    startFreeTrial__days: undefined,
    subscribe: undefined,
    subscriptionDetails: {
      beginsOn: undefined,
      currentBillingCycle: undefined,
      endsOn: undefined,
      firstPaymentAmount: undefined,
      firstPaymentOn: undefined,
      nextPaymentAmount: undefined,
      nextPaymentOn: undefined,
      pastDueAt: undefined,
      renewsAt: undefined,
      subscribedOn: undefined,
      title: undefined,
      trialEndsOn: undefined,
      trialStartedOn: undefined,
    },
    subtotal: undefined,
    switchPlan: undefined,
    switchToAnnual: undefined,
    switchToAnnualWithAnnualPrice: undefined,
    switchToMonthly: undefined,
    switchToMonthlyWithPrice: undefined,
    totalDue: undefined,
    totalDueToday: undefined,
    viewFeatures: undefined,
    viewPayment: undefined,
    year: undefined,
  },
  createOrganization: {
    formButtonSubmit: '조직 만들기',
    invitePage: {
      formButtonReset: '넘기기',
    },
    title: '조직 만들기',
  },
  dates: {
    lastDay: "어제 {{ date | timeString('ko-KR') }}",
    next6Days: "다음 {{ date | weekday('ko-KR','long') }} {{ date | timeString('ko-KR') }}",
    nextDay: "내일 {{ date | timeString('ko-KR') }}",
    numeric: "{{ date | numeric('ko-KR') }}",
    previous6Days: "지난 {{ date | weekday('ko-KR','long') }} {{ date | timeString('ko-KR') }}",
    sameDay: "오늘 {{ date | timeString('ko-KR') }}",
  },
  dividerText: '또는',
  footerActionLink__alternativePhoneCodeProvider: undefined,
  footerActionLink__useAnotherMethod: '다른 방법 사용하기',
  footerPageLink__help: '도움',
  footerPageLink__privacy: '개인정보처리방침',
  footerPageLink__terms: '약관',
  formButtonPrimary: '계속',
  formButtonPrimary__verify: 'Verify',
  formFieldAction__forgotPassword: '비밀번호를 잊으셨나요?',
  formFieldError__matchingPasswords: '비밀번호가 일치합니다.',
  formFieldError__notMatchingPasswords: '비밀번호가 일치하지 않습니다.',
  formFieldError__verificationLinkExpired: 'The verification link expired. Please request a new link.',
  formFieldHintText__optional: '선택사항',
  formFieldHintText__slug: 'A slug is a human-readable ID that must be unique. It’s often used in URLs.',
  formFieldInputPlaceholder__apiKeyDescription: undefined,
  formFieldInputPlaceholder__apiKeyExpirationDate: undefined,
  formFieldInputPlaceholder__apiKeyName: undefined,
  formFieldInputPlaceholder__backupCode: undefined,
  formFieldInputPlaceholder__confirmDeletionUserAccount: '계정 삭제',
  formFieldInputPlaceholder__emailAddress: undefined,
  formFieldInputPlaceholder__emailAddress_username: undefined,
  formFieldInputPlaceholder__emailAddresses:
    '하나 이상의 이메일 주소를 공백 또는 쉼표로 구분하여 입력하거나 붙여넣습니다',
  formFieldInputPlaceholder__firstName: undefined,
  formFieldInputPlaceholder__lastName: undefined,
  formFieldInputPlaceholder__organizationDomain: undefined,
  formFieldInputPlaceholder__organizationDomainEmailAddress: undefined,
  formFieldInputPlaceholder__organizationName: undefined,
  formFieldInputPlaceholder__organizationSlug: undefined,
  formFieldInputPlaceholder__password: undefined,
  formFieldInputPlaceholder__phoneNumber: undefined,
  formFieldInputPlaceholder__username: undefined,
  formFieldLabel__apiKeyDescription: undefined,
  formFieldLabel__apiKeyExpiration: undefined,
  formFieldLabel__apiKeyName: undefined,
  formFieldLabel__automaticInvitations: 'Enable automatic invitations for this domain',
  formFieldLabel__backupCode: '백업 코드',
  formFieldLabel__confirmDeletion: '확인',
  formFieldLabel__confirmPassword: '비밀번호 확인',
  formFieldLabel__currentPassword: '현재 비밀번호',
  formFieldLabel__emailAddress: '이메일 주소',
  formFieldLabel__emailAddress_username: '이메일 주소 혹은 사용자 이름',
  formFieldLabel__emailAddresses: '이메일 주소',
  formFieldLabel__firstName: '이름',
  formFieldLabel__lastName: '성',
  formFieldLabel__newPassword: '새 비밀번호',
  formFieldLabel__organizationDomain: '도메인',
  formFieldLabel__organizationDomainDeletePending: 'Delete pending invitations and suggestions',
  formFieldLabel__organizationDomainEmailAddress: 'Verification email address',
  formFieldLabel__organizationDomainEmailAddressDescription:
    'Enter an email address under this domain to receive a code and verify this domain.',
  formFieldLabel__organizationName: '이름',
  formFieldLabel__organizationSlug: '슬러그',
  formFieldLabel__passkeyName: undefined,
  formFieldLabel__password: '비밀번호',
  formFieldLabel__phoneNumber: '휴대폰 번호',
  formFieldLabel__role: '역할',
  formFieldLabel__signOutOfOtherSessions: '다른 모든 기기에서 로그아웃',
  formFieldLabel__username: '사용자 이름',
  impersonationFab: {
    action__signOut: '로그아웃',
    title: '{{identifier}}로 로그인했습니다',
  },
  maintenanceMode: undefined,
  membershipRole__admin: '관리자',
  membershipRole__basicMember: '멤버',
  membershipRole__guestMember: '게스트',
  organizationList: {
    action__createOrganization: 'Create organization',
    action__invitationAccept: 'Join',
    action__suggestionsAccept: 'Request to join',
    createOrganization: 'Create Organization',
    invitationAcceptedLabel: 'Joined',
    subtitle: 'to continue to {{applicationName}}',
    suggestionsAcceptedLabel: 'Pending approval',
    title: 'Choose an account',
    titleWithoutPersonal: 'Choose an organization',
  },
  organizationProfile: {
    apiKeysPage: {
      title: undefined,
    },
    badge__automaticInvitation: 'Automatic invitations',
    badge__automaticSuggestion: 'Automatic suggestions',
    badge__manualInvitation: 'No automatic enrollment',
    badge__unverified: 'Unverified',
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    createDomainPage: {
      subtitle:
        'Add the domain to verify. Users with email addresses at this domain can join the organization automatically or request to join.',
      title: 'Add domain',
    },
    invitePage: {
      detailsTitle__inviteFailed: '초대를 보낼 수 없습니다. 다음을 수정하고 다시 시도하세요:',
      formButtonPrimary__continue: '초대 보내기',
      selectDropdown__role: 'Select role',
      subtitle: '이 조직에 새 멤버 초대',
      successMessage: '초대가 성공적으로 전송되었습니다',
      title: '회원 초대',
    },
    membersPage: {
      action__invite: '초대',
      action__search: undefined,
      activeMembersTab: {
        menuAction__remove: '회원 제거',
        tableHeader__actions: undefined,
        tableHeader__joined: '가입됨',
        tableHeader__role: '역할',
        tableHeader__user: '사용자',
      },
      detailsTitle__emptyRow: '표시할 멤버 없음',
      invitationsTab: {
        autoInvitations: {
          headerSubtitle:
            'Invite users by connecting an email domain with your organization. Anyone who signs up with a matching email domain will be able to join the organization anytime.',
          headerTitle: 'Automatic invitations',
          primaryButton: 'Manage verified domains',
        },
        table__emptyRow: 'No invitations to display',
      },
      invitedMembersTab: {
        menuAction__revoke: '초대 취소',
        tableHeader__invited: '초대됨',
      },
      requestsTab: {
        autoSuggestions: {
          headerSubtitle:
            'Users who sign up with a matching email domain, will be able to see a suggestion to request to join your organization.',
          headerTitle: 'Automatic suggestions',
          primaryButton: 'Manage verified domains',
        },
        menuAction__approve: 'Approve',
        menuAction__reject: 'Reject',
        tableHeader__requested: 'Requested access',
        table__emptyRow: 'No requests to display',
      },
      start: {
        headerTitle__invitations: 'Invitations',
        headerTitle__members: 'Members',
        headerTitle__requests: 'Requests',
      },
    },
    navbar: {
      apiKeys: undefined,
      billing: undefined,
      description: 'Manage your organization.',
      general: 'General',
      members: 'Members',
      title: 'Organization',
    },
    plansPage: {
      alerts: {
        noPermissionsToManageBilling: undefined,
      },
      title: undefined,
    },
    profilePage: {
      dangerSection: {
        deleteOrganization: {
          actionDescription: '계속하려면 아래에 {{organizationName}}을(를) 입력하세요.',
          messageLine1: '이 조직을 삭제하시겠습니까?',
          messageLine2: '이 작업은 영구적이며 되돌릴 수 없습니다.',
          successMessage: '조직을 삭제했습니다.',
          title: '조직 삭제',
        },
        leaveOrganization: {
          actionDescription: 'Type "{{organizationName}}" below to continue.',
          messageLine1: '이 조직을 탈퇴하시겠습니까? 이 조직 및 해당 애플리케이션에 대한 액세스 권한을 잃게됩니다.',
          messageLine2: '이 작업은 영구적이며 되돌릴 수 없습니다',
          successMessage: '조직을 탈퇴했습니다.',
          title: '조직 떠나기',
        },
        title: '위험',
      },
      domainSection: {
        menuAction__manage: 'Manage',
        menuAction__remove: 'Delete',
        menuAction__verify: 'Verify',
        primaryButton: '도메인 추가',
        subtitle: '인증된 이메일 도메인을 기반으로 사용자가 조직에 자동으로 가입하거나 가입 요청을 할 수 있게 합니다.',
        title: '인증된 도메인',
      },
      successMessage: '조직이 업데이트되었습니다.',
      title: '조직 프로필',
    },
    removeDomainPage: {
      messageLine1: 'The email domain {{domain}} will be removed.',
      messageLine2: 'Users won’t be able to join the organization automatically after this.',
      successMessage: '{{domain}} has been removed.',
      title: 'Remove domain',
    },
    start: {
      headerTitle__general: 'General',
      headerTitle__members: '멤버',
      profileSection: {
        primaryButton: undefined,
        title: 'Organization Profile',
        uploadAction__title: 'Logo',
      },
    },
    verifiedDomainPage: {
      dangerTab: {
        calloutInfoLabel: 'Removing this domain will affect invited users.',
        removeDomainActionLabel__remove: 'Remove domain',
        removeDomainSubtitle: 'Remove this domain from your verified domains',
        removeDomainTitle: 'Remove domain',
      },
      enrollmentTab: {
        automaticInvitationOption__description:
          'Users are automatically invited to join the organization when they sign-up and can join anytime.',
        automaticInvitationOption__label: 'Automatic invitations',
        automaticSuggestionOption__description:
          'Users receive a suggestion to request to join, but must be approved by an admin before they are able to join the organization.',
        automaticSuggestionOption__label: 'Automatic suggestions',
        calloutInfoLabel: 'Changing the enrollment mode will only affect new users.',
        calloutInvitationCountLabel: 'Pending invitations sent to users: {{count}}',
        calloutSuggestionCountLabel: 'Pending suggestions sent to users: {{count}}',
        manualInvitationOption__description: 'Users can only be invited manually to the organization.',
        manualInvitationOption__label: 'No automatic enrollment',
        subtitle: 'Choose how users from this domain can join the organization.',
      },
      start: {
        headerTitle__danger: 'Danger',
        headerTitle__enrollment: 'Enrollment options',
      },
      subtitle: 'The domain {{domain}} is now verified. Continue by selecting enrollment mode.',
      title: 'Update {{domain}}',
    },
    verifyDomainPage: {
      formSubtitle: 'Enter the verification code sent to your email address',
      formTitle: 'Verification code',
      resendButton: "Didn't receive a code? Resend",
      subtitle: 'The domain {{domainName}} needs to be verified via email.',
      subtitleVerificationCodeScreen: 'A verification code was sent to {{emailAddress}}. Enter the code to continue.',
      title: 'Verify domain',
    },
  },
  organizationSwitcher: {
    action__createOrganization: '조직 만들기',
    action__invitationAccept: 'Join',
    action__manageOrganization: '조직 관리',
    action__suggestionsAccept: 'Request to join',
    notSelected: '선택한 조직 없음',
    personalWorkspace: '개인 워크스페이스',
    suggestionsAcceptedLabel: 'Pending approval',
  },
  paginationButton__next: '다음',
  paginationButton__previous: '이전',
  paginationRowText__displaying: '표시중',
  paginationRowText__of: '의',
  reverification: {
    alternativeMethods: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__backupCode: undefined,
      blockButton__emailCode: undefined,
      blockButton__passkey: undefined,
      blockButton__password: undefined,
      blockButton__phoneCode: undefined,
      blockButton__totp: undefined,
      getHelp: {
        blockButton__emailSupport: undefined,
        content: undefined,
        title: undefined,
      },
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: undefined,
      title: undefined,
    },
    emailCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    noAvailableMethods: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
    passkey: {
      blockButton__passkey: undefined,
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCode: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    phoneCodeMfa: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    totpMfa: {
      formTitle: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
  signIn: {
    accountSwitcher: {
      action__addAccount: 'Add account',
      action__signOutAll: 'Sign out of all accounts',
      subtitle: 'Select the account with which you wish to continue.',
      title: 'Choose an account',
    },
    alternativeMethods: {
      actionLink: '도움 요청하기',
      actionText: 'Don’t have any of these?',
      blockButton__backupCode: '백업 코드 사용하기',
      blockButton__emailCode: '{{identifier}}로 이메일 코드 보내기',
      blockButton__emailLink: '{{identifier}}로 이메일 링크 보내기',
      blockButton__passkey: undefined,
      blockButton__password: '비밀번호로 로그인',
      blockButton__phoneCode: '{{identifier}}로 SMS 코드 보내기',
      blockButton__totp: '인증 앱 사용하기',
      getHelp: {
        blockButton__emailSupport: '이메일 지원',
        content: '계정에 로그인하는 데 문제가 있는 경우 이메일을 보내주시면 최대한 빨리 액세스를 복구해 드리겠습니다.',
        title: '도움 요청하기',
      },
      subtitle: '문제가 있나요? 다른 방법으로 로그인할 수 있습니다.',
      title: '다른 방법 사용하기',
    },
    alternativePhoneCodeProvider: {
      formTitle: undefined,
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    backupCodeMfa: {
      subtitle: '백업코드는 2단계 인증을 설정할 때 얻은 코드입니다',
      title: '백업 코드 입력',
    },
    emailCode: {
      formTitle: '인증 코드',
      resendButton: '코드 재전송',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '이메일을 확인하세요',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      expired: {
        subtitle: '계속하려면 원래 탭으로 돌아가세요.',
        title: '이 인증 링크는 만료되었습니다',
      },
      failed: {
        subtitle: '계속하려면 원래 탭으로 돌아가세요.',
        title: '이 인증 링크는 유효하지 않습니다',
      },
      formSubtitle: '이메일로 전송된 인증 링크를 사용하세요',
      formTitle: '인증 링크',
      loading: {
        subtitle: '곧 리다이렉션 됩니다',
        title: '로그인 중...',
      },
      resendButton: '링크 재전송',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '이메일을 확인하세요',
      unusedTab: {
        title: '이 탭을 닫으셔도 됩니다',
      },
      verified: {
        subtitle: '곧 리다이렉션 될 예정입니다',
        title: '로그인에 성공했습니다',
      },
      verifiedSwitchTab: {
        subtitle: '계속하려면 원래 탭으로 돌아가세요',
        subtitleNewTab: '계속하려면 새로 연 탭으로 돌아가세요',
        titleNewTab: '다른 탭에서 로그인',
      },
    },
    forgotPassword: {
      formTitle: '비밀번호 재설정 코드',
      resendButton: '코드 재전송',
      subtitle: 'to reset your password',
      subtitle_email: '먼저 이메일로 전송된 코드를 입력하세요',
      subtitle_phone: '먼저 휴대폰으로 전송된 코드를 입력하세요',
      title: '비밀번호 재설정',
    },
    forgotPasswordAlternativeMethods: {
      blockButton__resetPassword: '비밀번호 재설정',
      label__alternativeMethods: '또는 다른 방법으로 로그인',
      title: '비밀번호를 잊으셨나요?',
    },
    noAvailableMethods: {
      message: '로그인을 계속할 수 없습니다. 사용 가능한 인증 방법이 없습니다.',
      subtitle: '오류가 발생했습니다',
      title: '로그인할 수 없습니다',
    },
    passkey: {
      subtitle: undefined,
      title: undefined,
    },
    password: {
      actionLink: '다른 방법 사용하기',
      subtitle: '계정에 등록된 비밀번호를 입력해 주세요',
      title: '비밀번호를 입력하세요',
    },
    passwordPwned: {
      title: undefined,
    },
    phoneCode: {
      formTitle: '인증 코드',
      resendButton: '코드 다시 보내기',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '휴대폰 확인',
    },
    phoneCodeMfa: {
      formTitle: '인증 코드',
      resendButton: '코드 다시 보내기',
      subtitle: '계속하려면 휴대폰으로 전송된 인증 코드를 입력하세요',
      title: '휴대폰 확인',
    },
    resetPassword: {
      formButtonPrimary: '비밀번호 재설정',
      requiredMessage: 'For security reasons, it is required to reset your password.',
      successMessage: '비밀번호가 성공적으로 변경되었습니다. 로그인하는 중입니다. 잠시만 기다려주세요.',
      title: '비밀번호 재설정',
    },
    resetPasswordMfa: {
      detailsLabel: '비밀번호를 재설정하기 전에 신원을 확인해야 합니다.',
    },
    start: {
      actionLink: '회원가입',
      actionLink__join_waitlist: undefined,
      actionLink__use_email: '이메일 사용하기',
      actionLink__use_email_username: '이메일 또는 사용자 이름 사용하기',
      actionLink__use_passkey: undefined,
      actionLink__use_phone: '휴대폰 번호 사용하기',
      actionLink__use_username: '사용자 이름 사용하기',
      actionText: '계정이 없으신가요?',
      actionText__join_waitlist: undefined,
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '환영합니다! 계속하려면 로그인해 주세요',
      subtitleCombined: undefined,
      title: '{{applicationName}}에 로그인',
      titleCombined: undefined,
    },
    totpMfa: {
      formTitle: '인증 코드',
      subtitle: '계속하려면 인증 앱에서 생성된 인증 코드를 입력하세요',
      title: '2단계 인증',
    },
  },
  signInEnterPasswordTitle: '비밀번호를 입력하세요',
  signUp: {
    alternativePhoneCodeProvider: {
      resendButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    continue: {
      actionLink: '로그인',
      actionText: '계정이 있으신가요?',
      subtitle: '계속하려면 누락된 필드에 값을 입력하세요',
      title: '누락된 필드를 입력하세요',
    },
    emailCode: {
      formSubtitle: '이메일 주소로 전송된 인증 코드를 입력하세요',
      formTitle: '인증 코드',
      resendButton: '코드 재전송',
      subtitle: '이메일로 전송된 인증 코드를 입력하세요',
      title: '이메일 인증',
    },
    emailLink: {
      clientMismatch: {
        subtitle: undefined,
        title: undefined,
      },
      formSubtitle: '이메일 주소로 전송된 인증 링크를 사용합니다.',
      formTitle: '인증 링크',
      loading: {
        title: '가입 중...',
      },
      resendButton: '링크 재전송',
      subtitle: '{{applicationName}}로 계속하려면',
      title: '이메일 인증하기',
      verified: {
        title: '성공적으로 가입에 성공했습니다',
      },
      verifiedSwitchTab: {
        subtitle: '계속하려면 새로 연 탭으로 돌아가기',
        subtitleNewTab: '계속하려면 이전 탭으로 돌아가기',
        title: '이메일 인증 성공',
      },
    },
    legalConsent: {
      checkbox: {
        label__onlyPrivacyPolicy: undefined,
        label__onlyTermsOfService: undefined,
        label__termsOfServiceAndPrivacyPolicy: undefined,
      },
      continue: {
        subtitle: undefined,
        title: undefined,
      },
    },
    phoneCode: {
      formSubtitle: '휴대폰 번호로 전송된 인증 코드를 입력하세요.',
      formTitle: '인증 코드',
      resendButton: '코드 재전송',
      subtitle: '휴대폰으로 전송된 인증 코드를 입력하세요',
      title: '휴대폰 번호 인증',
    },
    restrictedAccess: {
      actionLink: undefined,
      actionText: undefined,
      blockButton__emailSupport: undefined,
      blockButton__joinWaitlist: undefined,
      subtitle: undefined,
      subtitleWaitlist: undefined,
      title: undefined,
    },
    start: {
      actionLink: '로그인하기',
      actionLink__use_email: undefined,
      actionLink__use_phone: undefined,
      actionText: '계정이 있으신가요?',
      alternativePhoneCodeProvider: {
        actionLink: undefined,
        label: undefined,
        subtitle: undefined,
        title: undefined,
      },
      subtitle: '환영합니다! 아래 정보를 입력해주세요.',
      subtitleCombined: '환영합니다! 아래 정보를 입력해주세요.',
      title: '계정 만들기',
      titleCombined: '계정 만들기',
    },
  },
  socialButtonsBlockButton: '{{provider|titleize}}로 계속하기',
  socialButtonsBlockButtonManyInView: undefined,
  taskChooseOrganization: {
    chooseOrganization: {
      action__createOrganization: undefined,
      action__invitationAccept: undefined,
      action__suggestionsAccept: undefined,
      subtitle: undefined,
      suggestionsAcceptedLabel: undefined,
      title: undefined,
    },
    createOrganization: {
      formButtonReset: undefined,
      formButtonSubmit: undefined,
      formFieldInputPlaceholder__name: undefined,
      formFieldInputPlaceholder__slug: undefined,
      formFieldLabel__name: undefined,
      formFieldLabel__slug: undefined,
      subtitle: undefined,
      title: undefined,
    },
    signOut: {
      actionLink: undefined,
      actionText: undefined,
    },
  },
  unstable__errors: {
    already_a_member_in_organization: undefined,
    captcha_invalid:
      'Sign up unsuccessful due to failed security validations. Please refresh the page to try again or reach out to support for more assistance.',
    captcha_unavailable:
      'Sign up unsuccessful due to failed bot validation. Please refresh the page to try again or reach out to support for more assistance.',
    form_code_incorrect: undefined,
    form_identifier_exists__email_address: undefined,
    form_identifier_exists__phone_number: undefined,
    form_identifier_exists__username: undefined,
    form_identifier_not_found: '이 세부 정보와 일치하는 계정을 찾을 수 없습니다.',
    form_param_format_invalid: undefined,
    form_param_format_invalid__email_address: 'Email address must be a valid email address.',
    form_param_format_invalid__phone_number: 'Phone number must be in a valid international format',
    form_param_max_length_exceeded__first_name: 'First name should not exceed 256 characters.',
    form_param_max_length_exceeded__last_name: 'Last name should not exceed 256 characters.',
    form_param_max_length_exceeded__name: 'Name should not exceed 256 characters.',
    form_param_nil: undefined,
    form_param_type_invalid: undefined,
    form_param_type_invalid__email_address: undefined,
    form_param_type_invalid__phone_number: undefined,
    form_param_value_invalid: undefined,
    form_password_incorrect: undefined,
    form_password_length_too_short: undefined,
    form_password_not_strong_enough: '비밀번호가 충분히 안전하지 않습니다.',
    form_password_pwned: '이 비밀번호는 유출사항이 발견되어 사용할 수 없으므로 대신 다른 비밀번호를 사용해 보세요.',
    form_password_pwned__sign_in: undefined,
    form_password_size_in_bytes_exceeded:
      '비밀번호가 허용되는 최대 바이트 수를 초과했습니다. 비밀번호를 줄이거나 일부 특수 문자를 제거해 주세요.',
    form_password_validation_failed: '잘못된 비밀번호',
    form_username_invalid_character: undefined,
    form_username_invalid_length: undefined,
    identification_deletion_failed: 'You cannot delete your last identification.',
    not_allowed_access:
      "이메일 주소 또는 전화번호는 가입에 사용할 수 없습니다. 이는 '+', '=', '#' 또는 '.'이 이메일 주소에 사용되었거나 임시 이메일 서비스에 연결된 도메인이 사용되었거나 명시적 제외가 있는 경우입니다. 이 오류가 발생한 경우 지원에 문의하세요.",
    organization_domain_blocked: undefined,
    organization_domain_common: undefined,
    organization_domain_exists_for_enterprise_connection: undefined,
    organization_membership_quota_exceeded: undefined,
    organization_minimum_permissions_needed: undefined,
    organization_not_found_or_unauthorized: undefined,
    organization_not_found_or_unauthorized_with_create_organization_disabled: undefined,
    passkey_already_exists: undefined,
    passkey_not_supported: undefined,
    passkey_pa_not_supported: undefined,
    passkey_registration_cancelled: undefined,
    passkey_retrieval_cancelled: undefined,
    passwordComplexity: {
      maximumLength: '{{length}} 보다 짧은 문자열',
      minimumLength: '{{length}} 또는 그 이상의 문자열',
      requireLowercase: '소문자',
      requireNumbers: '숫자',
      requireSpecialCharacter: '특수문자',
      requireUppercase: '대문자',
      sentencePrefix: '당신의 비밀번호는 반드시 포함해야합니다',
    },
    phone_number_exists: '이 전화번호는 이미 사용중입니다. 다른 번호를 시도해 주세요.',
    session_exists: '이미 로그인 중입니다.',
    web3_missing_identifier: undefined,
    zxcvbn: {
      couldBeStronger: '비밀번호는 작동하지만 더 강력할 수 있습니다. 문자를 더 추가해 보세요.',
      goodPassword: '수고하셨습니다. 훌륭한 비밀번호입니다.',
      notEnough: '비밀번호가 충분히 안전하지 않습니다.',
      suggestions: {
        allUppercase: '모든 문자가 아닌 일부 문자를 대문자로 표시합니다.',
        anotherWord: '덜 일반적인 단어를 더 추가합니다.',
        associatedYears: '귀하와 연관된 연도는 피하세요.',
        capitalization: '한 글자 이상을 대문자로 표기하세요.',
        dates: '자신과 관련된 날짜와 연도는 피하세요.',
        l33t: "'a'에서 '@'와 같이 예측 가능한 문자 대체를 피하세요.",
        longerKeyboardPattern: '더 긴 키보드 패턴을 사용하고 타이핑 방향을 여러 번 변경합니다.',
        noNeed: '기호, 숫자 또는 대문자를 사용하지 않고도 강력한 비밀번호를 만들 수 있습니다.',
        pwned: '이 비밀번호를 다른 곳에서 사용하는 경우 변경해야 합니다.',
        recentYears: '최근 연도는 피하세요.',
        repeated: '반복되는 단어와 문자를 피하세요.',
        reverseWords: '일반적인 단어의 철자를 거꾸로 쓰지 마세요.',
        sequences: '일반적인 문자 시퀀스를 피하세요.',
        useWords: '여러 단어를 사용하되 일반적인 문구는 피하세요.',
      },
      warnings: {
        common: '일반적으로 사용되는 비밀번호입니다.',
        commonNames: '일반적인 이름과 성은 추측하기 쉽습니다.',
        dates: '날짜는 쉽게 추측할 수 있습니다.',
        extendedRepeat: '"abcabcabc"와 같이 반복되는 문자 패턴은 쉽게 추측할 수 있습니다.',
        keyPattern: '짧은 패턴은 추측하기 쉽습니다.',
        namesByThemselves: '단일 이름이나 성은 추측하기 쉽습니다.',
        pwned: '인터넷에서 데이터 유출로 인해 비밀번호가 노출되었습니다.',
        recentYears: '최근연도는 쉽게 추측할 수 있습니다.',
        sequences: '"abc"와 같은 일반적인 문자 시퀀스는 쉽게 추측할 수 있습니다.',
        similarToCommon: '일반적으로 사용되는 비밀번호와 유사합니다.',
        simpleRepeat: '"aaa"와 같이 반복되는 문자는 쉽게 추측할 수 있습니다.',
        straightRow: '같은 줄에 위치한 키보드를 사용하는 것은 추측하기 쉽습니다.',
        topHundred: '자주 사용되는 비밀번호입니다.',
        topTen: '아주 많이 사용되는 비밀번호입니다.',
        userInputs: '개인 정보나 페이지 관련 데이터가 없어야 합니다.',
        wordByItself: '한 단어는 쉽게 추측할 수 있습니다.',
      },
    },
  },
  userButton: {
    action__addAccount: '계정 추가',
    action__manageAccount: '계정 관리',
    action__signOut: '로그아웃',
    action__signOutAll: '모든 계정에서 로그아웃',
  },
  userProfile: {
    apiKeysPage: {
      title: undefined,
    },
    backupCodePage: {
      actionLabel__copied: '복사 완료!',
      actionLabel__copy: '전체 복사',
      actionLabel__download: '.txt 다운로드',
      actionLabel__print: '인쇄',
      infoText1: '이 계정에 대해 백업 코드가 활성화됩니다.',
      infoText2:
        '백업 코드를 비밀로 유지하고 안전하게 보관하세요. 백업 코드가 손상된 것으로 의심되는 경우 백업 코드를 다시 생성할 수 있습니다.',
      subtitle__codelist: '안전하게 저장하고 비밀로 유지하세요.',
      successMessage:
        '이제 백업 코드가 활성화되었습니다. 인증 장치에 액세스할 수 없는 경우 이 중 하나를 사용하여 계정에 로그인할 수 있습니다. 각 코드는 한 번만 사용할 수 있습니다.',
      successSubtitle: '인증 장치에 액세스할 수 없는 경우 이 중 하나를 사용하여 계정에 로그인할 수 있습니다.',
      title: '백업 코드 인증 추가',
      title__codelist: '백업 코드',
    },
    billingPage: {
      paymentHistorySection: {
        empty: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        tableHeader__status: undefined,
      },
      paymentSourcesSection: {
        actionLabel__default: undefined,
        actionLabel__remove: undefined,
        add: undefined,
        addSubtitle: undefined,
        cancelButton: undefined,
        formButtonPrimary__add: undefined,
        formButtonPrimary__pay: undefined,
        payWithTestCardButton: undefined,
        removeResource: {
          messageLine1: undefined,
          messageLine2: undefined,
          successMessage: undefined,
          title: undefined,
        },
        title: undefined,
      },
      start: {
        headerTitle__payments: undefined,
        headerTitle__plans: undefined,
        headerTitle__statements: undefined,
        headerTitle__subscriptions: undefined,
      },
      statementsSection: {
        empty: undefined,
        itemCaption__paidForPlan: undefined,
        itemCaption__proratedCredit: undefined,
        itemCaption__subscribedAndPaidForPlan: undefined,
        notFound: undefined,
        tableHeader__amount: undefined,
        tableHeader__date: undefined,
        title: undefined,
        totalPaid: undefined,
      },
      subscriptionsListSection: {
        actionLabel__newSubscription: undefined,
        actionLabel__switchPlan: undefined,
        tableHeader__edit: undefined,
        tableHeader__plan: undefined,
        tableHeader__startDate: undefined,
        title: undefined,
      },
      subscriptionsSection: {
        actionLabel__default: undefined,
      },
      switchPlansSection: {
        title: undefined,
      },
      title: undefined,
    },
    connectedAccountPage: {
      formHint: '계정을 연결할 제공자를 선택하세요',
      formHint__noAccounts: '사용 가능한 외부 계정 제공자가 없습니다.',
      removeResource: {
        messageLine1: '{{identifier}}가 이 계정에서 제거 될 예정입니다.',
        messageLine2: '이 연결된 계정을 더 이상 사용할 수 없으며 종속된 모든 기능이 더 이상 작동하지 않습니다.',
        successMessage: '{{connectedAccount}}가 당신의 계정에서 제거되었습니다.',
        title: '연결된 계정 제거',
      },
      socialButtonsBlockButton: '{{provider|titleize}} 계정 연결',
      successMessage: '이 제공자가 계정에 추가되었습니다.',
      title: '연결된 계정 추가하기',
    },
    deletePage: {
      actionDescription: '계속하려면 아래에 계정 삭제를 입력하세요.',
      confirm: '계정 삭제',
      messageLine1: '정말로 계정을 삭제하시겠습니까?',
      messageLine2: '이 작업은 영구적이며 되돌릴 수 없습니다.',
      title: '계정 삭제',
    },
    emailAddressPage: {
      emailCode: {
        formHint: '이 이메일 주소로 인증 코드가 포함된 이메일이 전송됩니다.',
        formSubtitle: '{{identifier}}에게 전송된 인증 코드를 입력합니다',
        formTitle: '인증 코드',
        resendButton: '코드 재전송',
        successMessage: '{{identifier}} 이메일이 당신의 계정에 추가되었습니다.',
      },
      emailLink: {
        formHint: '인증 링크가 포함된 이메일이 이 이메일 주소로 전송됩니다.',
        formSubtitle: '{{identifier}}에게 전송된 이메일의 링크를 클릭합니다.',
        formTitle: '인증 링크',
        resendButton: '링크 재전송',
        successMessage: '{{identifier}} 이메일이 당신의 계정에 추가되었습니다.',
      },
      enterpriseSSOLink: {
        formButton: undefined,
        formSubtitle: undefined,
      },
      formHint: undefined,
      removeResource: {
        messageLine1: '{{identifier}} 이메일이 이 계정에서 제거 될 에정입니다.',
        messageLine2: '더 이상 이 이메일 주소로 로그인할 수 없습니다.',
        successMessage: '{{emailAddress}} 이메일이 당신의 계정에서 삭제되었습니다',
        title: '이메일 제거',
      },
      title: '이메일 주소 추가',
      verifyTitle: 'Verify email address',
    },
    formButtonPrimary__add: 'Add',
    formButtonPrimary__continue: '계속',
    formButtonPrimary__finish: '완료',
    formButtonPrimary__remove: 'Remove',
    formButtonPrimary__save: 'Save',
    formButtonReset: '취소',
    mfaPage: {
      formHint: '추가할 방법을 선택합니다.',
      title: '2단계 인증 추가',
    },
    mfaPhoneCodePage: {
      backButton: 'Use existing number',
      primaryButton__addPhoneNumber: '휴대폰 번호 추가',
      removeResource: {
        messageLine1: '{{identifier}}는 로그인할 때 더 이상 인증 코드를 받지 않습니다.',
        messageLine2: '계정이 안전하지 않을 수 있습니다. 계속하시겠습니까?',
        successMessage: '{{mfaPhoneCode}}에 대한 SMS 코드 2단계 인증이 제거되었습니다',
        title: '2단계 인증 제거',
      },
      subtitle__availablePhoneNumbers: 'SMS 코드 2단계 인증을 위해 등록할 휴대폰 번호를 선택하세요.',
      subtitle__unavailablePhoneNumbers: 'SMS 코드 2단계 인증에 등록할 수 있는 휴대폰 번호가 없습니다.',
      successMessage1:
        'When signing in, you will need to enter a verification code sent to this phone number as an additional step.',
      successMessage2:
        'Save these backup codes and store them somewhere safe. If you lose access to your authentication device, you can use backup codes to sign in.',
      successTitle: 'SMS code verification enabled',
      title: 'SMS 코드 인증 추가',
    },
    mfaTOTPPage: {
      authenticatorApp: {
        buttonAbleToScan__nonPrimary: '대신 QR 코드 스캔 하세요',
        buttonUnableToScan__nonPrimary: 'QR 코드를 스캔할 수 없나요?',
        infoText__ableToScan: '인증 앱에서 새 로그인 방법을 설정하고 다음 QR 코드를 스캔하여 계정에 연결합니다.',
        infoText__unableToScan: '인증 장치에서 새 로그인 방법을 설정하고 아래에 제공된 키를 입력합니다.',
        inputLabel__unableToScan1:
          '시간 기반 또는 일회용 비밀번호가 사용 설정되어 있는지 확인한 다음 계정 연결을 완료합니다.',
        inputLabel__unableToScan2: '또는 인증자가 TOTP URI를 지원하는 경우 전체 URI를 복사할 수도 있습니다.',
      },
      removeResource: {
        messageLine1: '로그인할 때 더 이상 이 인증자의 인증 코드가 필요하지 않습니다.',
        messageLine2: '계정이 안전하지 않을 수 있습니다. 계속 진행하시겠습니까?',
        successMessage: '인증자 애플리케이션을 통한 2단계 인증이 제거되었습니다.',
        title: '2단계 인증 제거',
      },
      successMessage:
        '이제 2단계 인증이 활성화되었습니다. 로그인할 때 추가 단계로 이 인증자의 인증 코드를 입력해야 합니다.',
      title: '인증 애플리케이션 추가',
      verifySubtitle: '인증자가 생성한 인증 코드를 입력합니다',
      verifyTitle: '인증 코드',
    },
    mobileButton__menu: '메뉴',
    navbar: {
      account: '프로필',
      apiKeys: undefined,
      billing: undefined,
      description: '계정 정보를 관리하세요.',
      security: '보안',
      title: '계정',
    },
    passkeyScreen: {
      removeResource: {
        messageLine1: undefined,
        title: undefined,
      },
      subtitle__rename: undefined,
      title__rename: undefined,
    },
    passwordPage: {
      checkboxInfoText__signOutOfOtherSessions:
        'It is recommended to sign out of all other devices which may have used your old password.',
      readonly: 'Your password can currently not be edited because you can sign in only via the enterprise connection.',
      successMessage__set: '비밀번호가 설정되었습니다.',
      successMessage__signOutOfOtherSessions: '다른 모든 기기는 로그아웃되었습니다.',
      successMessage__update: '비밀번호가 업데이트되었습니다.',
      title__set: '비밀번호 설정',
      title__update: '비밀번호 변경',
    },
    phoneNumberPage: {
      infoText: '인증 링크가 포함된 문자 메시지가 이 휴대폰 번호로 전송됩니다.',
      removeResource: {
        messageLine1: '{{identifier}}가 당신의 계정에서 제거 될 예정입니다.',
        messageLine2: '더 이상 이 휴대폰 번호로 로그인할 수 없습니다.',
        successMessage: '{{phoneNumber}}가 당신의 계정에서 제거되었습니다.',
        title: '휴대폰 번호 제거',
      },
      successMessage: '{{identifier}}가 당신의 계정에 추가되었습니다.',
      title: '휴대폰 번호 추가',
      verifySubtitle: 'Enter the verification code sent to {{identifier}}',
      verifyTitle: 'Verify phone number',
    },
    plansPage: {
      title: undefined,
    },
    profilePage: {
      fileDropAreaHint: '10MB보다 작은 JPG, PNG, GIF 또는 WEBP 이미지를 업로드합니다',
      imageFormDestructiveActionSubtitle: '이미지 제거',
      imageFormSubtitle: '이미지 업로드',
      imageFormTitle: '프로필 이미지',
      readonly: 'Your profile information has been provided by the enterprise connection and cannot be edited.',
      successMessage: '프로필이 업데이트되었습니다.',
      title: '프로필 업데이트',
    },
    start: {
      activeDevicesSection: {
        destructiveAction: '이 장치에서 로그아웃',
        title: '활성화된 장치',
      },
      connectedAccountsSection: {
        actionLabel__connectionFailed: '재시도',
        actionLabel__reauthorize: '지금 인증하기',
        destructiveActionTitle: '제거',
        primaryButton: '계정 연결하기',
        subtitle__disconnected: undefined,
        subtitle__reauthorize:
          'The required scopes have been updated, and you may be experiencing limited functionality. Please re-authorize this application to avoid any issues',
        title: '연결된 계정',
      },
      dangerSection: {
        deleteAccountButton: '계정 삭제',
        title: '위험',
      },
      emailAddressesSection: {
        destructiveAction: '이메일 주소 제거',
        detailsAction__nonPrimary: '기본으로 설정',
        detailsAction__primary: '인증 완료',
        detailsAction__unverified: '인증 완료',
        primaryButton: '이메일 주소 추가',
        title: '이메일 주소',
      },
      enterpriseAccountsSection: {
        title: '기업 계정',
      },
      headerTitle__account: '프로필',
      headerTitle__security: '보안',
      mfaSection: {
        backupCodes: {
          actionLabel__regenerate: '코드 재생성',
          headerTitle: '백업 코드',
          subtitle__regenerate: '새로운 보안 백업 코드 세트를 받으세요. 이전 백업 코드는 삭제되며 사용할 수 없습니다.',
          title__regenerate: '백업 코드 재생성',
        },
        phoneCode: {
          actionLabel__setDefault: '기본으로 설정',
          destructiveActionLabel: '휴대폰 번호 제거',
        },
        primaryButton: '2단계 인증 추가',
        title: '2단계 인증',
        totp: {
          destructiveActionTitle: '제거',
          headerTitle: '인증 애플리케이션',
        },
      },
      passkeysSection: {
        menuAction__destructive: undefined,
        menuAction__rename: undefined,
        primaryButton: undefined,
        title: undefined,
      },
      passwordSection: {
        primaryButton__setPassword: '비밀번호 설정',
        primaryButton__updatePassword: '비밀번호 변경',
        title: '비밀번호',
      },
      phoneNumbersSection: {
        destructiveAction: '휴대폰 번호 제거',
        detailsAction__nonPrimary: '기본으로 설정',
        detailsAction__primary: '인증 완료',
        detailsAction__unverified: '인증 완료',
        primaryButton: '휴대폰 번호 추가',
        title: '휴대폰 번호',
      },
      profileSection: {
        primaryButton: '프로필 수정',
        title: '프로필',
      },
      usernameSection: {
        primaryButton__setUsername: '사용자 이름 설정',
        primaryButton__updateUsername: '사용자 이름 변경',
        title: '사용자 이름',
      },
      web3WalletsSection: {
        destructiveAction: '지갑 제거',
        detailsAction__nonPrimary: undefined,
        primaryButton: 'Web3 지갑',
        title: 'Web3 지갑',
      },
    },
    usernamePage: {
      successMessage: '당신읭 사용자 이름이 업데이트되었습니다.',
      title__set: '사용자 이름 업데이트',
      title__update: '사용자 이름 업데이트',
    },
    web3WalletPage: {
      removeResource: {
        messageLine1: '{{identifier}}가 이 계정에서 제거 될 예정입니다.',
        messageLine2: '더 이상 이 web3 지갑을 사용하여 로그인할 수 없습니다.',
        successMessage: '{{web3Wallet}}가 당신의 계정에서 제거되었습니다.',
        title: 'web3 지갑 제거',
      },
      subtitle__availableWallets: '계정에 연결할 web3 지갑을 선택하세요.',
      subtitle__unavailableWallets: '사용 가능한 web3 지갑이 없습니다.',
      successMessage: '지갑이 계정에 추가되었습니다ß.',
      title: 'web3 지갑 추가',
      web3WalletButtonsBlockButton: undefined,
    },
  },
  waitlist: {
    start: {
      actionLink: undefined,
      actionText: undefined,
      formButton: undefined,
      subtitle: undefined,
      title: undefined,
    },
    success: {
      message: undefined,
      subtitle: undefined,
      title: undefined,
    },
  },
} as const;
